import { IconSubtask } from '@tabler/icons-react';
import { RevisionDevice } from 'components/forms/revision/fill-out/types';
import { IFormSection } from 'pages/revisions-module/template-editor/editors/form/types';
import { useMemo } from 'react';
import { useFillOutRevisionDataProvider } from 'components/forms/revision/fill-out/data/FillOutRevisionDataProvider';
import { useDeviceMeasurement } from 'components/forms/revision/fill-out/modals/device-measurement-modal/DeviceMeasurementProvider';
import Button from 'components/Button';
import { useFillOutRevisionSaveDraftProvider } from 'components/forms/revision/fill-out/data/FillOutRevisionSaveDraftProvider';

interface DeviceMeasurementButtonProps {
  device: RevisionDevice;
  measurement: IFormSection;
}

/**
 * A button which opens a dialog for specifying device measurements.
 */
export default function DeviceMeasurementButton({
  device: { deviceId, measurements: measurementMap },
  measurement: measurementSection,
}: DeviceMeasurementButtonProps) {
  const { revision, form } = useFillOutRevisionDataProvider();
  const { specifyMeasurements } = useDeviceMeasurement();
  const { saveDraft } = useFillOutRevisionSaveDraftProvider();

  const measurements = useMemo(() => measurementMap[measurementSection.id] ?? [], [measurementMap, measurementSection]);

  const spec = useMemo(
    () => measurementSection.rows.map(({ inputs }) => inputs.map(({ spec }) => spec).flat()).flat() as any[],
    [measurementSection]
  );

  /** Opens the measurements modal. */
  const openMeasurementsModal = () =>
    specifyMeasurements({
      measurementType: measurementSection.name,
      deviceTypeId: revision.deviceType.deviceTypeId,
      spec,
      lockingEnabled: measurementSection.measurementsAllowRowLocking ?? false,
      initialValues: { measurements },
      onSubmit: ({ measurements }) => {
        const nextData = {
          ...form.values,
          devices: {
            ...form.values.devices,
            [deviceId]: {
              ...form.values.devices[deviceId],
              measurements: {
                ...form.values.devices[deviceId].measurements,
                [measurementSection.id]: measurements,
              },
            },
          },
        };
        form.setValues(nextData);
        saveDraft({ notify: false, overrideData: nextData });
      },
    });

  return (
    <Button size="md" variant="subtle" leftIcon={<IconSubtask stroke="1.5" />} onClick={openMeasurementsModal}>
      {measurementSection.name} {measurements.length > 0 && `(${measurements.length})`}
    </Button>
  );
}
